import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, List, ListItem, TextField } from '@mui/material';
import { BRAND } from 'config/fe-routes';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { setGlobalLoader } from 'store/actions/global-loader.actions';
import { Brand } from 'types/brand.types';
import { toKebabCase } from 'utils/format-text';

import A from '../../Link/Link';
import Loader from '../../Loader/Loader';
import Modal from '../../Modal/Modal';

type BrandModalProps = {
  open: boolean;
  title: string;
  icon: string;
  close: () => void;
  brands: Brand[];
};

const BRAND_FILTER_DEBOUNCE_PERIOD = 250;

const BrandsModal = (props: BrandModalProps) => {
  const { open, title, icon, close, brands } = props;

  const [brandsModalSearchValue, setBrandsModalSearchValue] = useState<string>('');
  const [filteredBrands, setFilteredBrands] = useState<Brand[]>(brands);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const handleClose = () => {
    setBrandsModalSearchValue('');
    close();
  };

  const handleSearch = useCallback(
    debounce((searchValue) => {
      const foundBrands = brands?.filter((brand) => brand.name.toLowerCase().includes(searchValue.toLowerCase()));

      setFilteredBrands(foundBrands);
      setIsSearching(false);
    }, BRAND_FILTER_DEBOUNCE_PERIOD),
    [],
  );

  useEffect(() => {
    setIsSearching(true);
    handleSearch(brandsModalSearchValue);
  }, [brandsModalSearchValue]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      translateTitle={false}
      titleUnderline={false}
      alignTitleStart={true}
      title={title}
      titleIcon={icon}
      fullWidth={true}
      maxWidth="xs"
      className="brands-modal"
      contentClassName="brands-modal__content"
    >
      <TextField
        fullWidth={true}
        color="secondary"
        variant="outlined"
        size="small"
        InputProps={{
          className: 'brands-modal__search',
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon className="search-icon" />
            </InputAdornment>
          ),
        }}
        value={brandsModalSearchValue || ''}
        onChange={(event) => setBrandsModalSearchValue(event.target.value)}
      />

      <List dense={false} disablePadding={true} className="brands-modal__list">
        {isSearching && <Loader className="brands-modal__list-loader" />}

        {!isSearching &&
          filteredBrands &&
          filteredBrands?.map((brand) => {
            const brandLink = BRAND(toKebabCase(brand.name));

            return (
              <ListItem key={brand.id} disableGutters={true} className="brands-modal__list-item">
                <A
                  href={brandLink.href}
                  as={brandLink.as}
                  onClick={(e) => {
                    handleClose();
                    setGlobalLoader(true, e);
                  }}
                >
                  {brand.name}
                </A>
              </ListItem>
            );
          })}
      </List>
    </Modal>
  );
};

export default BrandsModal;
